import React, { ReactNode } from 'react'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import {
  formatDateWithFormat,
  ignoreTimezoneDate,
  sameDay,
} from '../../../helpers/date'
import { EventAttendancePreview } from './EventAttendancePreview'
import { EventDetailViewTabs } from './EventDetailView'
import { IBoardRoomEventWithFiles } from '../../../interfaces/services/boardroom-events.interfaces'
import { IEventWithFiles } from '../../../interfaces/services/events.interfaces'
import { Divider } from '../../../components/common/Divider'
import { downloadFile } from '../../../helpers/download'
import { getDocumentDownloadName } from '../../../helpers/documents'
import { Icon, IconChoices } from '../../../components/common/Icon'

interface EventDetailRowProps {
  title: string
  description?: string
  content?: JSX.Element
}

export const EventDetailRow = ({
  title,
  content,
  description,
}: EventDetailRowProps): ReactNode => {
  return (
    <div className="flex flex-col space-y-2">
      <p className="text-neutral-30" style={theme.textVariants.lead}>
        {title}
      </p>
      {description && <p style={theme.textVariants.base}>{description}</p>}
      {content && content}
    </div>
  )
}

interface EventDetailInformationProps {
  event: IBoardRoomEventWithFiles | IEventWithFiles
  boardRoomEvent: boolean | undefined
  setActiveTab: (tab: EventDetailViewTabs) => void
}

export const EventDetailInformation = ({
  event,
  boardRoomEvent,
  setActiveTab,
}: EventDetailInformationProps): ReactNode => {
  const { translate } = useAppTranslation()
  const documents = event.documents ?? []

  return (
    <div className="w-full lg:w-1/2">
      <div className="flex flex-col gap-4">
        {event.isAllDay ? (
          <EventDetailRow
            title={translate('eventDetailView.headers.date')}
            description={`${formatDateWithFormat(
              ignoreTimezoneDate(new Date(event.startDate)),
              'd MMMM yyyy'
            )}${
              !sameDay(new Date(event.startDate), new Date(event.endDate)) &&
              event.endDate > event.startDate
                ? ` - ${formatDateWithFormat(
                    ignoreTimezoneDate(new Date(event.endDate)),
                    'd MMMM yyyy'
                  )}`
                : ''
            }
              `}
          />
        ) : (
          <>
            <EventDetailRow
              title={translate('createUpdateEvent.form.labels.startDate')}
              description={formatDateWithFormat(
                ignoreTimezoneDate(new Date(event.startDate)),
                "d MMMM yyyy 'kl.' HH:mm"
              )}
            />
            <EventDetailRow
              title={translate('createUpdateEvent.form.labels.endDate')}
              description={formatDateWithFormat(
                ignoreTimezoneDate(new Date(event.endDate)),
                "d MMMM yyyy 'kl.' HH:mm"
              )}
            />
          </>
        )}
        {event.location && (
          <EventDetailRow
            title={translate('createUpdateEvent.form.labels.location')}
            description={event.location}
          />
        )}
        {event.description && (
          <EventDetailRow
            title={translate('createUpdateEvent.form.labels.description')}
            description={event.description}
          />
        )}
        {documents.length > 0 && (
          <>
            <Divider />
            <div className="flex flex-col gap-2">
              <p style={theme.textVariants.h3}>
                {translate('createUpdateContractForm.headers.documents')}
              </p>
              <div className="flex flex-col gap-2 xl:w-1/2">
                {documents.map((document) => (
                  <button
                    key={document.id}
                    className={`flex min-w-0 flex-row items-center gap-3 whitespace-nowrap rounded 
                                        bg-neutral-96 py-4 px-5`}
                    onClick={() =>
                      downloadFile(
                        document.url,
                        getDocumentDownloadName(document)
                      )
                    }
                  >
                    <Icon icon={IconChoices.DOCUMENT} size={24} />
                    <span
                      className="overflow-hidden text-ellipsis"
                      style={theme.textVariants.baseBold}
                    >
                      {document.name}
                    </span>
                    <Icon icon={IconChoices.ARROW} size={24} highlighted />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
        {event.attendanceEnabled && (
          <EventAttendancePreview
            event={event}
            boardRoomEvent={boardRoomEvent}
            setActiveTab={setActiveTab}
          />
        )}
      </div>
    </div>
  )
}
